import * as countries from "i18n-iso-countries";
import van, { type State } from "vanjs-core";
import { state } from "./state";

countries.registerLocale(require("i18n-iso-countries/langs/de.json"));
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));
countries.registerLocale(require("i18n-iso-countries/langs/sv.json"));

interface LeafNode {
	"de-DE": string;
	"en-GB": string;
	"fr-FR": string;
	"sv-SE": string;
}

// DEPRECATED
interface LocalizedString {
	enGB: string;
	deDE: string;
	frFR: string;
	svSE: string;
}

function dateString(date: Date | number | string): string {
	if (typeof date === "number" || typeof date === "string")
		date = new Date(date);
	return date.toLocaleDateString(activeLocale);
}

function dateTimeString(date: Date | number | string): string {
	if (typeof date === "number" || typeof date === "string")
		date = new Date(date);
	return `${dateString(date)} ${date.toLocaleTimeString(activeLocale)}`;
}

function kitchenTimeString(date: Date | string): string {
	if (typeof date === "string") date = new Date(date);
	const hour = date.getHours().toString().padStart(2, "0");
	const minute = date.getMinutes().toString().padStart(2, "0");
	return `${hour}:${minute}`;
}

export function relativeTimeString(date: Date | string): string {
	if (date === "") return "";
	if (typeof date === "string") date = new Date(date);
	const now = new Date();
	const elapsed = date.getTime() - now.getTime();
	const absElapsed = Math.abs(elapsed);

	const second = 1000;
	const minute = second * 60;
	const hour = minute * 60;
	const day = hour * 24;
	const month = day * 30;
	const year = month * 12;

	const rtf = new Intl.RelativeTimeFormat(activeLocale, {
		numeric: "auto",
	});

	if (absElapsed < minute)
		return rtf.format(Math.round(elapsed / second), "second");

	if (absElapsed < hour)
		return rtf.format(Math.round(elapsed / minute), "minute");

	if (absElapsed < day) return rtf.format(Math.round(elapsed / hour), "hour");

	if (absElapsed < month) return rtf.format(Math.round(elapsed / day), "day");

	if (absElapsed < year)
		return rtf.format(Math.round(elapsed / month), "month");

	return rtf.format(Math.round(elapsed / year), "year");
}

// DEPRECATED
function string(s: LocalizedString): string {
	switch (activeLocale) {
		case "de-DE":
			return s.deDE;
		case "fr-FR":
			return s.frFR;
		case "sv-SE":
			return s.svSE;
		default:
			return s.enGB;
	}
}

export function t(
	key: string,
	replacements: Record<string, string> = {},
): () => string {
	const keyParts = key.split(".");
	let currentNode: LocaleNode = localeRootNode;
	while (!("en-GB" in currentNode)) {
		const nextKeyPart = keyParts.shift();
		if (!nextKeyPart) throw `missing locale entry: ${key}`;

		currentNode = currentNode[nextKeyPart];
		if (!currentNode) throw `missing locale entry: ${key}`;
	}

	return () => {
		if (!currentNode["en-GB"]) throw "";
		let s = currentNode[activeLocale2.val];
		Object.entries(replacements).forEach(([placeholder, value]) => {
			s = s.replaceAll(`{${placeholder}}`, value);
		});
		return s;
	};
}

// DEPRECATED
export async function update(): Promise<void> {
	const supportedLocales: SupportedLocale[] = [
		"de-DE",
		"en-GB",
		"fr-FR",
		"sv-SE",
	];

	const user = await state.getSignedInUser();
	activeLocale = supportedLocales.includes(user.Locale)
		? user.Locale
		: "en-GB";

	activeLocale2.val = activeLocale;
	locale.strings = strings[activeLocale];
	document.documentElement.lang = activeLocale.substring(0, 2);
}

// DEPRECATED
export function weekdays(): string[] {
	switch (activeLocale) {
		case "de-DE":
			return [
				"Sonntag", // Sunday
				"Montag", // Monday
				"Dienstag", // Tuesday
				"Mittwoch", // Wednesday
				"Donnerstag", // Thursday
				"Freitag", // Friday
				"Samstag", // Saturday
			];
		case "fr-FR":
			return [
				"dimanche", // Sunday
				"lundi", // Monday
				"mardi", // Tuesday
				"mercredi", // Wednesday
				"jeudi", // Thursday
				"vendredi", // Friday
				"samedi", // Saturday
			];
		case "sv-SE":
			return [
				"söndag", // Sunday
				"måndag", // Monday
				"tisdag", // Tuesday
				"onsdag", // Wednesday
				"torsdag", // Thursday
				"fredag", // Friday
				"lördag", // Saturday
			];
		default:
			return [
				"Sunday",
				"Monday",
				"Tuesday",
				"Wednesday",
				"Thursday",
				"Friday",
				"Saturday",
			];
	}
}

// DEPRECATED
export let activeLocale: SupportedLocale = "en-GB";
const activeLocale2: State<SupportedLocale> = van.state("en-GB");

type InternalNode = {
	[key: string]: LocaleNode;
} & {
	[K in SupportedLocale]?: never;
};

type LocaleNode = InternalNode | LeafNode;

const localeRootNode: LocaleNode = {
	configureSp2Page: {
		fotaButton: {
			latestVersion: {
				"de-DE": "Neueste Version",
				"en-GB": "Latest version",
				"fr-FR": "Dernière version",
				"sv-SE": "Senaste versionen",
			},
			update: {
				"de-DE": "Aktualisieren",
				"en-GB": "Update",
				"fr-FR": "Mettre à jour",
				"sv-SE": "Uppdatera",
			},
		},
		fotaLabel: {
			"de-DE": "Firmware",
			"en-GB": "Firmware",
			"fr-FR": "Firmware",
			"sv-SE": "Firmware",
		},
	},
	deviceCard: {
		connection: {
			"de-DE": "- Verbindung",
			"en-GB": "- Connection",
			"fr-FR": "- Connexion",
			"sv-SE": "- Anslutning",
		},
		connectionMajorDisruptions: {
			"de-DE": "Starke Störungen",
			"en-GB": "Major disruptions",
			"fr-FR": "Perturbations majeures",
			"sv-SE": "Stora störningar",
		},
		connectionMinorDisruptions: {
			"de-DE": "Geringe Störungen",
			"en-GB": "Minor disruptions",
			"fr-FR": "Perturbations mineures",
			"sv-SE": "Mindre störningar",
		},
		connectionReliable: {
			"de-DE": "Zuverlässig",
			"en-GB": "Reliable",
			"fr-FR": "Fiable",
			"sv-SE": "Stabil",
		},
		lastActive: {
			"de-DE": "- Zuletzt aktiv",
			"en-GB": "- Last active",
			"fr-FR": "- Dernière activité",
			"sv-SE": "- Senast aktiv",
		},
	},
	deviceSettingsPage: {
		backButton: {
			"de-DE": "Zurück",
			"en-GB": "Back",
			"fr-FR": "Retour",
			"sv-SE": "Tillbaka",
		},
		confirmUnregistrationModal: {
			cancelButton: {
				"de-DE": "Abbrechen",
				"en-GB": "Cancel",
				"fr-FR": "Annuler",
				"sv-SE": "Avbryt",
			},
			confirmButton: {
				"de-DE": "Abmeldung",
				"en-GB": "Confirm",
				"fr-FR": "Confirmer",
				"sv-SE": "Bekräfta",
			},
			header: {
				"de-DE": "Abmeldung bestätigen",
				"en-GB": "Confirm unregistration",
				"fr-FR": "Confirmer la désinscription",
				"sv-SE": "Bekräfta avregistrering",
			},
		},
		nameLabel: {
			"de-DE": "Name",
			"en-GB": "Name",
			"fr-FR": "Nom",
			"sv-SE": "Namn",
		},
		ownerLabel: {
			"de-DE": "Eigentümer",
			"en-GB": "Owner",
			"fr-FR": "Propriétaire",
			"sv-SE": "Ägare",
		},
		saveButton: {
			"de-DE": "Speichern",
			"en-GB": "Save",
			"fr-FR": "Enregistrer",
			"sv-SE": "Spara",
		},
		sharingField: {
			inviteButton: {
				"de-DE": "Einladen",
				"en-GB": "Invite",
				"fr-FR": "Inviter",
				"sv-SE": "Bjud in",
			},
			label: {
				"de-DE": "Teilen",
				"en-GB": "Sharing",
				"fr-FR": "Partage",
				"sv-SE": "Delning",
			},
		},
		subscriptionLabel: {
			"de-DE": "Abonnement",
			"en-GB": "Subscription",
			"fr-FR": "Abonnement",
			"sv-SE": "Abonnemang",
		},
		tagsField: {
			addButton: {
				"de-DE": "Hinzufügen",
				"en-GB": "Add",
				"fr-FR": "Ajouter",
				"sv-SE": "Lägg till",
			},
			label: {
				"de-DE": "Tags",
				"en-GB": "Tags",
				"fr-FR": "Mots-clés",
				"sv-SE": "Taggar",
			},
		},
		unregisterButton: {
			"de-DE": "Registrierung aufheben",
			"en-GB": "Unregister",
			"fr-FR": "Désenregistrer",
			"sv-SE": "Avregistrera",
		},
	},
	deviceUnregistrationDisclaimer: {
		"de-DE":
			"Durch Klicken auf „Bestätigen“ erklären Sie sich damit einverstanden, dieses Gerät von Ihrem Konto abzumelden, und dass jegliche verbleibende Abonnementlaufzeit oder andere kostenpflichtige Funktionen, die mit diesem Gerät verknüpft sind, mit sofortiger Wirkung und ohne Entschädigung verloren gehen.",
		"en-GB":
			"By clicking confirm you acknowledge that you wish to unregister this device from your account and that any remaining subscription time or other paid for features associated with this device will be lost without compensation with immediate effect.",
		"fr-FR":
			"En cliquant sur confirmer, vous reconnaissez que vous souhaitez désenregistrer cet appareil de votre compte et que toute période d'abonnement restante ou autre fonctionnalité payante associée à cet appareil sera perdue sans compensation avec effet immédiat.",
		"sv-SE":
			"Genom att klicka på bekräfta godkänner du att du vill avregistrera denna enhet från ditt konto och att eventuell återstående prenumerationstid eller andra betalda funktioner kopplade till denna enhet går förlorade med omedelbar verkan utan ersättning.",
	},
	loadingScreen: {
		abortButton: {
			"de-DE": "Abbrechen",
			"en-GB": "Abort",
			"fr-FR": "Annuler",
			"sv-SE": "Avbryt",
		},
	},
	price: {
		monthly: {
			"de-DE": "{price} {currency} pro Monat ohne MwSt",
			"en-GB": "{price} {currency} per month ex VAT",
			"fr-FR": "{price} {currency} par mois hors tva",
			"sv-SE": "{price} {currency} per månad ex moms",
		},
		yearly: {
			"de-DE": "{price} {currency} pro Jahr ohne MwSt",
			"en-GB": "{price} {currency} per year ex VAT",
			"fr-FR": "{price} {currency} par an hors tva",
			"sv-SE": "{price} {currency} per år ex moms",
		},
	},
	sp2Card: {
		off: {
			"de-DE": "Aus",
			"en-GB": "Off",
			"fr-FR": "Éteint",
			"sv-SE": "Av",
		},
		on: {
			"de-DE": "Ein",
			"en-GB": "On",
			"fr-FR": "Allumé",
			"sv-SE": "På",
		},
		power: {
			"de-DE": "- Leistung",
			"en-GB": "- Power",
			"fr-FR": "- Puissance",
			"sv-SE": "- Ström",
		},
	},
	sp2LogsPage: {
		backButton: {
			"de-DE": "Zurück",
			"en-GB": "Back",
			"fr-FR": "Retour",
			"sv-SE": "Tillbaka",
		},
		consumptionHeader: {
			"de-DE": "Verbrauch",
			"en-GB": "Consumption",
			"fr-FR": "Consommation",
			"sv-SE": "Förbrukning",
		},
		loadHeader: {
			"de-DE": "Stromlast",
			"en-GB": "Power load",
			"fr-FR": "Charge électrique",
			"sv-SE": "Belastning",
		},
	},
	sp2SettingsPage: {
		consumptionField: {
			label: {
				"de-DE": "Verbrauch",
				"en-GB": "Consumption",
				"fr-FR": "Consommation",
				"sv-SE": "Förbrukning",
			},
			resetButton: {
				"de-DE": "Zurücksetzen",
				"en-GB": "Reset",
				"fr-FR": "Réinitialiser",
				"sv-SE": "Återställ",
			},
		},
		createTimerRuleModal: {
			actionLabel: {
				"de-DE": "Aktion",
				"en-GB": "Action",
				"fr-FR": "Action",
				"sv-SE": "Åtgärd",
			},
			actionOptions: {
				turnOff: {
					"de-DE": "Ausschalten",
					"en-GB": "Turn off",
					"fr-FR": "Éteindre",
					"sv-SE": "Stäng av",
				},
				turnOn: {
					"de-DE": "Einschalten",
					"en-GB": "Turn on",
					"fr-FR": "Allumer",
					"sv-SE": "Sätt på",
				},
			},
			cancelButton: {
				"de-DE": "Abbrechen",
				"en-GB": "Cancel",
				"fr-FR": "Annuler",
				"sv-SE": "Avbryt",
			},
			createButton: {
				"de-DE": "Erstellen",
				"en-GB": "Create",
				"fr-FR": "Créer",
				"sv-SE": "Skapa",
			},
			daysLabel: {
				"de-DE": "Tage",
				"en-GB": "Days",
				"fr-FR": "Jours",
				"sv-SE": "Dagar",
			},
			header: {
				"de-DE": "Timer-Regel erstellen",
				"en-GB": "Create timer rule",
				"fr-FR": "Créer une règle de minuterie",
				"sv-SE": "Skapa timerregel",
			},
			timeLabel: {
				"de-DE": "Zeit",
				"en-GB": "Time",
				"fr-FR": "Temps",
				"sv-SE": "Tid",
			},
		},
		deviceBeepLabel: {
			"de-DE": "Geräte-Piepton",
			"en-GB": "Device beep",
			"fr-FR": "Bip de l'appareil",
			"sv-SE": "Enhetspipljud",
		},
		powerFailureLabel: {
			"de-DE": "Stromausfall-Benachrichtigungen",
			"en-GB": "Power failure notifications",
			"fr-FR": "Notifications de coupure de courant",
			"sv-SE": "Strömavbrottsaviseringar",
		},
		powerLoadLabel: {
			"de-DE": "Stromlast-Benachrichtigungen",
			"en-GB": "Power load notifications",
			"fr-FR": "Notifications de charge électrique",
			"sv-SE": "Belastningsnotiser",
		},
		timerField: {
			createRuleButton: {
				"de-DE": "Regel erstellen",
				"en-GB": "Create rule",
				"fr-FR": "Créer une règle",
				"sv-SE": "Skapa regel",
			},
			label: {
				"de-DE": "Timer",
				"en-GB": "Timer",
				"fr-FR": "Minuteur",
				"sv-SE": "Timer",
			},
			ruleTableActions: {
				turnOff: {
					"de-DE": "Ausschalten",
					"en-GB": "Turn off",
					"fr-FR": "Éteindre",
					"sv-SE": "Stäng av",
				},
				turnOn: {
					"de-DE": "Einschalten",
					"en-GB": "Turn on",
					"fr-FR": "Allumer",
					"sv-SE": "Sätt på",
				},
			},
		},
	},
};

interface LocaleMap {
	add: string;
	back: string;
	billing: string;
	cancel: string;
	confirm: string;
	country: string;
	createAccount: string;
	dateOfPurchase: string;
	done: string;
	edit: string;
	email: string;
	friday: string;
	language: string;
	monday: string;
	name: string;
	none: string;
	off: string;
	on: string;
	owner: string;
	password: string;
	phone: string;
	product: string;
	proofOfPurchase: string;
	register: string;
	resetPassword: string;
	retailer: string;
	saturday: string;
	save: string;
	serialNumber: string;
	signIn: string;
	subscription: string;
	sunday: string;
	tags: string;
	thursday: string;
	trial: string;
	tuesday: string;
	turnOff: string;
	turnOn: string;
	unregister: string;
	verificationCode: string;
	verifyAccount: string;
	voltage: string;
	warranties: string;
	wednesday: string;
}

// DEPRECATED
const strings: Record<SupportedLocale, LocaleMap> = {
	"de-DE": {
		add: "Hinzufügen",
		back: "Zurück",
		billing: "Abrechnung",
		cancel: "Abbrechen",
		confirm: "Bestätigen",
		country: "Land",
		createAccount: "Konto erstellen",
		dateOfPurchase: "Kaufdatum",
		done: "Fertig",
		edit: "Bearbeiten",
		email: "E-Mail",
		friday: "Freitag",
		language: "Sprache",
		monday: "Montag",
		name: "Name",
		none: "Keine",
		off: "Aus",
		on: "Ein",
		owner: "Besitzer",
		password: "Passwort",
		phone: "Telefon",
		product: "Produkt",
		proofOfPurchase: "Kaufbeleg",
		register: "Registrieren",
		resetPassword: "Passwort zurücksetzen",
		retailer: "Händler",
		saturday: "Samstag",
		save: "Speichern",
		serialNumber: "Seriennummer",
		signIn: "Anmelden",
		subscription: "Abonnement",
		sunday: "Sonntag",
		tags: "Tags",
		thursday: "Donnerstag",
		trial: "Probeabonnement",
		tuesday: "Dienstag",
		turnOff: "Schalte aus",
		turnOn: "Schalte ein",
		unregister: "Abmelden",
		verificationCode: "Verifizierungscode",
		verifyAccount: "Konto verifizieren",
		voltage: "Voltage",
		wednesday: "Mittwoch",
		warranties: "Garantien",
	},
	"en-GB": {
		add: "Add",
		back: "Back",
		billing: "Billing",
		cancel: "Cancel",
		confirm: "Confirm",
		country: "Country",
		createAccount: "Create account",
		dateOfPurchase: "Date of purchase",
		done: "Done",
		edit: "Edit",
		email: "Email",
		friday: "Friday",
		language: "Language",
		monday: "Monday",
		name: "Name",
		none: "None",
		off: "Off",
		on: "On",
		owner: "Owner",
		password: "Password",
		phone: "Phone",
		product: "Product",
		proofOfPurchase: "Proof of purchase",
		register: "Register",
		resetPassword: "Reset password",
		retailer: "Retailer",
		saturday: "Saturday",
		save: "Save",
		serialNumber: "Serial number",
		signIn: "Sign in",
		subscription: "Subscription",
		sunday: "Sunday",
		tags: "Tags",
		thursday: "Thursday",
		trial: "Trial",
		tuesday: "Tuesday",
		turnOff: "Turn off",
		turnOn: "Turn on",
		unregister: "Unregister",
		verificationCode: "Verification code",
		verifyAccount: "Verify account",
		voltage: "Voltage",
		warranties: "Warranties",
		wednesday: "Wednesday",
	},
	"fr-FR": {
		add: "Ajouter",
		back: "Retour",
		billing: "Facturation",
		cancel: "Annuler",
		confirm: "Confirmer",
		country: "Pays",
		createAccount: "Créer un compte",
		dateOfPurchase: "Date d'achat",
		done: "Terminé",
		edit: "Éditer",
		email: "E-mail",
		friday: "Vendredi",
		language: "Langue",
		monday: "Lundi",
		name: "Nom",
		none: "Aucun",
		off: "Éteint",
		on: "Allumé",
		owner: "Propriétaire",
		password: "Mot de passe",
		phone: "Téléphone",
		product: "Produit",
		proofOfPurchase: "Preuve d'achat",
		register: "S'inscrire",
		retailer: "Détaillant",
		resetPassword: "Réinitialiser le mot de passe",
		saturday: "Samedi",
		save: "Enregistrer",
		serialNumber: "Numéro de série",
		signIn: "Se connecter",
		subscription: "Abonnement",
		sunday: "Dimanche",
		tags: "Étiquettes",
		thursday: "Jeudi",
		trial: "Essai",
		tuesday: "Mardi",
		turnOff: "Éteins",
		turnOn: "Allume",
		unregister: "Se désinscrire",
		verificationCode: "Code de vérification",
		verifyAccount: "Vérifier le compte",
		voltage: "Tension",
		warranties: "Garanties",
		wednesday: "Mercredi",
	},
	"sv-SE": {
		add: "Lägg till",
		back: "Tillbaka",
		billing: "Betalning",
		cancel: "Avbryt",
		confirm: "Bekräfta",
		country: "Land",
		createAccount: "Skapa konto",
		dateOfPurchase: "Inköpsdatum",
		done: "Klar",
		edit: "Redigera",
		email: "E-post",
		friday: "Fredag",
		language: "Språk",
		monday: "Måndag",
		name: "Namn",
		none: "Ingen",
		off: "Av",
		on: "På",
		owner: "Ägare",
		password: "Lösenord",
		phone: "Telefon",
		product: "Produkt",
		proofOfPurchase: "Inköpsbevis",
		register: "Registrera",
		resetPassword: "Återställ lösenord",
		retailer: "Återförsäljare",
		saturday: "Lördag",
		save: "Spara",
		serialNumber: "Serienummer",
		signIn: "Logga in",
		subscription: "Prenumeration",
		sunday: "Söndag",
		tags: "Taggar",
		thursday: "Torsdag",
		trial: "Provperiod",
		tuesday: "Tisdag",
		turnOff: "Stäng av",
		turnOn: "Sätt på",
		unregister: "Avregistrera",
		verificationCode: "Verifikationskod",
		verifyAccount: "Verifiera konto",
		voltage: "Spänning",
		warranties: "Garantier",
		wednesday: "Onsdag",
	},
};

// biome-ignore lint/nursery/useExplicitType: DEPRECATED
export const locale = {
	countries,
	dateString,
	dateTimeString,
	kitchenTimeString,
	id: activeLocale,
	string,
	strings: strings[activeLocale],
	update,
	weekdays,
};

type SupportedLocale = "de-DE" | "en-GB" | "fr-FR" | "sv-SE";
